<template>
  <div>
    <v-layout>
      <v-flex xs3>
        <v-checkbox
          v-model="campaign.enablePacingPerBudget"
          label="期間別に配信ペースを設定する"
        />
      </v-flex>
      <v-flex xs7>
        <AdvancedSetting button-text="表示オプション" class="mt-2">
          <v-layout>
            <DateRangePicker
              :start="displayDate.startTemp"
              :end="displayDate.endTemp"
              @change="changeTempDisplayDateRange"
              no-slot
              justify-start
              class="ml-2"
            />
          </v-layout>
          <v-layout>
            <v-checkbox
              v-model="showRejectedBudget"
              label="却下された案件予算も表示する"
              class="ml-2"
            />
          </v-layout>
        </AdvancedSetting>
      </v-flex>
      <v-spacer />
    </v-layout>
    <v-slide-y-transition>
      <v-layout v-if="!campaign.enablePacingPerBudget" class="pt-4">
        <InventoryPace
          :budget="campaign.dailyLimitBudget"
          :percentage="campaign.evenPacePercentage"
          :type="campaign.pacingType"
          @setType="setType"
          @setPercentage="setPercentage"
          @setBudget="setBudget"
        />
      </v-layout>
    </v-slide-y-transition>
    <v-btn @click="isShowModal = true" class="location-group-button-text">
      案件予算追加・変更申請
    </v-btn>
    <div
      @click="reload"
      class="location-group-button location-group-button-refresh"
    >
      <v-icon small>refresh</v-icon> 更新
    </div>

    <IOModalEdit
      v-if="isShowModal && form.insertionOrder"
      v-model="isShowModal"
      :advertiser="advertiser"
      :can-edit="canEditItem"
      :loading="loading"
      :insertion-order="form.insertionOrder"
      :sales-account-list="salesAccountList"
      @success="isShowModal = false"
      @cancel="isShowModal = false"
    />
    <BudgetList
      v-if="displayDate.start != null && displayDate.end != null"
      :form="form"
      :show-rejected-budget="showRejectedBudget"
      :set-pace="campaign.enablePacingPerBudget"
      :display-start-date="displayStartDate"
      :display-end-date="displayEndDate"
      :resubmittable="!form.isNew"
      @closeAndEditInsertionOrder="$emit('closeAndEditInsertionOrder')"
      @clickAddButton="addCampaignBudget"
      class="pt-3"
    />
    <CampaignModalPageBudgetGoal :form="form" class="pt-4" />
  </div>
</template>

<script>
import AdvancedSetting from '@/components/Shared/CrudUI/AdvancedSetting'
import CampaignModalPageBudgetGoal from './CampaignModalPageBudgetGoal.vue'
import DateRangePicker from '@/components/Advertiser/Operation/IO/Form/FormDateRange'
import InventoryPace from '@/components/Advertiser/Operation/IO/CampaignModalPageBudgetInventoryPace'
import BudgetList from '@/components/Advertiser/Operation/IO/CampaignModalPageBudgetList'
import { Form } from '@/models/CampaignOperation'
import { PacingTypes } from '@/models/CampaignOperation/Constants'
import cloneDeep from 'lodash/cloneDeep'
import { getters } from '@/vuex/modules/advertiser/insertionOrders/constants'
import { getters as currentAdvertiserGetter } from '@/vuex/modules/currentAdvertiser/constants'
import IOModalEdit from './IOModalEdit'

export default {
  components: {
    AdvancedSetting,
    DateRangePicker,
    InventoryPace,
    BudgetList,
    CampaignModalPageBudgetGoal,
    IOModalEdit,
  },
  props: {
    form: {
      type: Object,
      default: () => {},
    },
    insertionOrder: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      showRejectedBudget: false,
      displayDate: {
        start: undefined,
        startTemp: '',
        end: undefined,
        endTemp: '',
      },
      isShowModal: false,
      copiedBudgets: cloneDeep(this.form.campaign.mergedCampaignBudgets),
    }
  },
  computed: {
    advertiser() {
      return this.$store.getters[currentAdvertiserGetter.currentAdvertiser]
    },
    loading() {
      return this.$store.getters[getters.loading]
    },
    salesAccountList() {
      return this.$store.getters[getters.salesAccountList]
    },
    campaign() {
      return this.form.campaign
    },
    displayStartDate() {
      return this.displayDate.start + ' 00:00:00'
    },
    displayEndDate() {
      return this.displayDate.end + ' 23:59:59'
    },
    errorMessageForBudget() {
      return this.form.insertionOrderBudgets.reduce((acc, budget, index) => {
        if (
          this.campaign.pacingType === PacingTypes.DAILY_LIMIT &&
          this.campaign.dailyLimitBudget <= 0
        ) {
          return `一日上限予算が設定されていません。\n一日上限予算は￥1以上に設定してください。`
        }
        if (acc) {
          return acc
        } else {
          return Form.getInsertionOrderBudgetViolations(
            budget,
            Form.getCampaignBudgets(
              this.campaign.mergedCampaignBudgets,
              budget,
            ),
            this.campaign.pacingType,
            this.campaign.dailyLimitBudget,
          ).reduce((acc2, violation, index2) => {
            if (acc2) {
              return acc2
            } else {
              if (violation.reversePeriod)
                return `案件予算${index + 1} - キャンペーン予算${index2 +
                  1}\n\n配信開始時刻が終了時刻より後になっています。\n配信開始・終了時刻が前後しないように修正してください。`
              else if (violation.zeroBudget)
                return `案件予算${index + 1} - キャンペーン予算${index2 +
                  1}\n\n上限予算が設定されていません。\n上限予算は￥1以上に設定してください。`
              else if (violation.overBudget)
                return `案件予算${index + 1} - キャンペーン予算${index2 +
                  1}\n\n上限予算が案件予算を超過しています。`
              else if (violation.overlapping.length > 0)
                return `案件予算${index + 1} - キャンペーン予算${index2 +
                  1}\n\nキャンペーン予算${violation.overlapping
                  .map(i => i + 1)
                  .join(
                    '、',
                  )}と配信期間が重複しています。\n配信期間が重複しないように修正してください。`
              else return ''
            }
          }, '')
        }
      }, '')
    },
    errorMessageForGoal() {
      return this.form.campaign.zeroGoalValueViolation &&
        this.form.campaign.goalType === 'cpc'
        ? '目標CPCは1以上を入力してください'
        : this.form.campaign.zeroGoalValueViolation &&
          this.form.campaign.goalType === 'ctr'
        ? '目標CTRは0.01 - 100%の間で入力してください。'
        : ''
    },
    infoMessageForCreativeSchedule() {
      return this.form.campaign.isCreativeScheduleViolation(this.copiedBudgets)
        ? `クリエイティブが期間ごとに設定されています。
            配信日程を変更した場合、クリエイティブの配信スケジュールを確認してください。`
        : ''
    },
    errorMessage() {
      return this.errorMessageForBudget || this.errorMessageForGoal
    },
    infoMessage() {
      return this.infoMessageForCreativeSchedule
    },
    canEditItem() {
      return (
        !this.form.insertionOrder.isCreatedOnAN &&
        (this.canEdit || this.form.insertionOrder.isEditable)
      )
    },
  },
  mounted() {
    // 初期表示の日付範囲を取得
    this.changeTempDisplayDateRange(this.form.wholePeriodOfBudgets)
  },
  methods: {
    async reload() {
      await this.form.fetchBudgetList
      await this.form.fetchInsertionOrderBudgets()
    },
    setPercentage(value) {
      this.campaign.evenPacePercentage = Number(value)
    },
    setType(value) {
      this.campaign.pacingType = value
    },
    setBudget(value) {
      this.campaign.dailyLimitBudget = Number(value)
    },
    changeTempDisplayDateRange(change) {
      this.displayDate.startTemp = change.startDate
      this.displayDate.endTemp = change.endDate
      this.changeDisplayDateRange()
    },
    changeDisplayDateRange() {
      this.displayDate.start = this.displayDate.startTemp
      this.displayDate.end = this.displayDate.endTemp
    },
    addCampaignBudget(item) {
      this.form.campaign.addCampaignBudget(item)
    },
  },
}
</script>

<style scoped lang="scss">
.location-group-button-text {
  color: #1976d2;
  margin-left: 5px;
  display: inline-block;
  cursor: pointer;
  &:hover {
    background-color: #d7eeff;
  }
}

.location-group-button {
  display: inline-block;
  cursor: pointer;
  padding: 3px 6px;
  &:hover {
    background-color: #d7eeff;
  }
}

.location-group-button-refresh {
  margin-left: 5px;
}
</style>
